// ** React Imports
import { Fragment, useState, MouseEvent, useEffect } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Table from '@mui/material/Table'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import { styled } from '@mui/material/styles'
import TimelineDot from '@mui/lab/TimelineDot'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TimelineItem from '@mui/lab/TimelineItem'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import TimelineContent from '@mui/lab/TimelineContent'
import TableContainer from '@mui/material/TableContainer'
import { v4 as uuid } from 'uuid';
import CustomChip from 'src/materio/@core/components/mui/chip'
// ** Types
import { ThemeColor } from 'src/materio/@core/layouts/types'
import { PlaybookLibrary, PlaybookStore, saveLibrary, loadPlaybooks } from '../PlaybookStore'
import { Button, IconButton, InputAdornment, Menu, MenuItem, TextField, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutline from 'mdi-material-ui/DeleteOutline'
import { Link } from 'react-router-dom'
import EyeOutline from 'mdi-material-ui/EyeOutline'
import DotsVertical from 'mdi-material-ui/DotsVertical'
import PencilOutline from 'mdi-material-ui/PencilOutline'
import Download from 'mdi-material-ui/Download'
import ContentCopy from 'mdi-material-ui/ContentCopy'
import NameDialog from '../NameDialog'
import EditMessageDialog from './EditMessageDialog'
import { client } from '../../../utils/axios-client'
import PageHeader from '../../../materio/@core/components/page-header'
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf'
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../../hooks/useAwaitableComponent'
import ConfirmationDialog from '../../common/ConfirmationDialog'
import { ApiResponseType } from '../../../types'
import ApplicationStore from '../../../stores/ApplicationStore'
import UXPageHeader from '../../../components/froms/UXPageHeader'
import UXButton from '../../../components/froms/UXButton'
import TableCell from '../../../components/table/TableCell'
import TableRow from '../../../components/table/TableRow'
import SearchIcon from '@mui/icons-material/Search';

interface ProjectListDataType {
    img: string
    hours: string
    totalTask: string
    projectType: string
    projectTitle: string
    progressValue: number
    progressColor: ThemeColor
}

const MessageList = () => {
    const playbook = PlaybookStore.useState(s => s.selectedPlaybook);
    const [addLibraryOpen, setAddLibraryOpen] = useState(false);
    const [editLibraryOpen, setEditLibraryOpen] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== "idle";
    const [filter, setFilter] = useState('');

    //useEffect(() => {
    //    onEdit({ id: '2a3d399a-9bca-4135-a75e-e49ba1150efd' } as any);
    //},[])

    const onAddLibrary = () => {
        setAddLibraryOpen(true);
    }

    const onSaveLibrary = async (row: PlaybookLibrary) => {
        if (playbook === null) throw 'Playbook not selected';

        const newRecord = { ...row, id: uuid() };
        const response = await saveLibrary(newRecord, playbook, '');
        
        if (response.succeeded) {
            setAddLibraryOpen(false);

            onEdit(newRecord)
        }

        return response;
    }

    const handleConfirmDelete = async (row: PlaybookLibrary) => {
        try {
            const value = await execute();
            const response = await client.delete<ApiResponseType>('/api/playbooks/library', { params: { messageId: row.id } });

            if (response.data.succeeded) {
                await loadPlaybooks('');

                ApplicationStore.update(s => {
                    s.snackbarMessage = "Message successfully deleted";
                    s.snackbarOpen = true;
                });
            } else {
                alert('Error while deleting message.');
            }
        } catch (err) {

        } finally {
            reset();
        }
    };

    const onEdit = async (row: PlaybookLibrary) => {
        const response = await client.get('/api/playbooks/library', { params: { id: row.id } });

        PlaybookStore.update(s => {
            s.selectedLibrary = response.data;
        })

        setEditLibraryOpen(true);
    }

    const onView = async (row: PlaybookLibrary) => {
        const response = await client.get('/api/playbooks/library', { params: { id: row.id } });

        PlaybookStore.update(s => {
            s.selectedLibrary = response.data;
        })

        setViewMode(true);
        setEditLibraryOpen(true);
    }

    const onEditLibraryClose = () => {
        setEditLibraryOpen(false);
        setViewMode(false);

        PlaybookStore.update(s => {
            s.selectedLibrary = null;
        })
    }

    const onCloseNewMessage = () => {
        setAddLibraryOpen(false)
    }

    let libraries = structuredClone(playbook?.libraries ?? []);
    let filteredLibraries = filter != '' ? libraries.filter(s => s.name.toLowerCase().includes(filter.toLowerCase())) : libraries;

    var collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    filteredLibraries.sort((a, b) => collator.compare(a.name, b.name))

    return (
        <Fragment>
            <Card sx={{ mb: 6 }}>
                <CardHeader title='Playbook Messaging' titleTypographyProps={{ variant: 'h6' }} action={
                    <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.AddPlaybookMessaging}>
                        <TextField size="small" sx={{ mr: 2 }} value={filter} onChange={(e) => setFilter(e.target.value)} InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}

                            inputProps={{
                                style: {
                                    padding: 4.5
                                }
                            }} />
                        <UXButton title="Add Message" onClick={onAddLibrary} variant='contained' sx={{ mr: 1 }} icon={<AddIcon />}>
                            Add Message
                        </UXButton>
                    </RenderIf>
                } />


                <Divider sx={{ m: 0 }} />

                <TableContainer>
                    <Table size='small'>
                        <TableHead
                            sx={{ backgroundColor: theme => (theme.palette.mode === 'light' ? 'grey.50' : 'background.default') }}
                        >
                            <TableRow>
                                <TableCell basis={70}>Name</TableCell>
                                <TableCell basis={30}></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredLibraries.map((item, index: number) => (
                                <TableRow hover key={index} sx={{ '&:last-of-type td': { border: 0 } }}>
                                    <TableCell basis={70}>
                                        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.name}</Typography>
                                    </TableCell>
                                    <TableCell basis={30}>
                                        <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.ViewPlaybookMessaging}>
                                            <Tooltip title='View'>
                                                <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={() => onView(item)}>
                                                    <EyeOutline fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                        </RenderIf>
                                        <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.DeletePlaybookMessaging}>
                                            <IconButton size="small" onClick={() => handleConfirmDelete(item)} title="Delete Message">
                                                <DeleteIcon fontSize='small' />
                                            </IconButton>
                                        </RenderIf>
                                        <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.EditPlaybookMessaging}>
                                            <Tooltip title='Edit'>
                                                <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={() => onEdit(item)}>
                                                    <EditIcon fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                        </RenderIf>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <NameDialog open={addLibraryOpen} onClose={onCloseNewMessage} onSubmit={onSaveLibrary} title="Create Message" subTitle="Please enter a name for the message" fieldLabel="Message Name" confirmButtonLabel="Create Message" record={{ name: '' } as PlaybookLibrary} />
            {editLibraryOpen && <EditMessageDialog open={editLibraryOpen} onClose={onEditLibraryClose} viewMode={viewMode} />}
            <ConfirmationDialog open={showModal} onConfirm={resolve} onCancel={reject} title="Delete?" message="Are you sure you want to delete the message?" confirmButtonText="Delete" cancelButtonText="Cancel" />
        </Fragment >
    )
}

export default MessageList
